import moment from "moment";
import LZString from "lz-string";

import { genericApi, hasAnyStored } from "../lib/genericAPI";
import { whoami } from "../lib/api/_base";
import { userstype } from "../lib/storage/users";
import { inspectionlabelreqtype } from "../lib/storage/inspectionlabelrequirements";
import { inspectiontype } from "../lib/storage/inspection";
import { orchardtype } from "../lib/storage/orchard";
import { producertype } from "../lib/storage/producers";
import { puctype } from "../lib/storage/pucs";
import { piordergroupedtype } from "../lib/storage/piordergrouped";
import { piinstructiontype } from "../lib/storage/piinstructions";
import { phtreatmenttype } from "../lib/storage/phtreatment";
import { targetmarkettype } from "../lib/storage/targetmarket";
import { phlabeltype } from "../lib/storage/phlabel";
import { specialmarkettype } from "../lib/storage/specialmarket";
import { varietiestype } from "../lib/storage/varieties";
import { weekstype } from "../lib/storage/week";
import { labtesttype } from "../lib/storage/labtest";
import { producerphtype, producerphStorageName } from "../lib/storage/producerph";

import { producerph, getProducerPHFullDetail, GetProducerPHFullDetailType } from "../lib/api/producerph";
import { labtest, labtestFull } from "../lib/api/labtest";
import { users } from "../lib/api/users";
import { producers } from "../lib/api/producer";
import { pucs } from "../lib/api/producerpuc";
import { orchards } from "../lib/api/producerorchard";
import { target, targetsDistinctMarketsCountries } from "../lib/api/target";
import { getOrdersGrouped, piorder, getOrders } from "../lib/api/piorder";
import { inspections, findByNumber } from "../lib/api/inspection";
import { inspectionorders } from "../lib/api/inspectionorders";
import { inspectionactions } from "../lib/api/inspectionactions";
import { phtreatment } from "../lib/api/phtreatment";
import { phlabel } from "../lib/api/phlabel";
import { inspectionlabelrequirements } from "../lib/api/inspectionlabelrequirement";
import { specialmarkets } from "../lib/api/specialmarket";
import { varietyFull } from "../lib/api/variety";
import { weeks } from "../lib/api/week";
import { pack, PackGenericType } from "../lib/api/pack";

// Tables we expect to always have data on a healthy device. If any of these
// are empty, we treat the device as "fresh" and force a full re-sync of every
// table regardless of the lastSync watermark. Producers is the primary canary
// since every device must have producer data to function.
const CANARY_TABLES = ["producers"];

const isDeviceMissingCriticalData = (): boolean => CANARY_TABLES.some((name) => !hasAnyStored(name));

const safe = async (fn: () => Promise<unknown>): Promise<void> => {
  try {
    await fn();
  } catch (err) {
    // Swallow individual sync errors so a single failure doesn't abort the
    // overall sync. The caller surfaces a generic completion state.
    // eslint-disable-next-line no-console
    console.error("sync step failed", err);
  }
};

const checkUsers = async (lastSync: string | number) => {
  const newData = await users.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("users");
  const usersGeneric = genericApi<userstype>("users");
  const data = await users.all();
  await usersGeneric.createMany(data.map((item) => ({ id: item.id, username: item.username, admin: item.admin })));
};

const checkProducers = async (lastSync: string | number) => {
  const newData = await producers.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("producers");
  const producersGeneric = genericApi<producertype>("producers");
  const data = await producers.all();
  await producersGeneric.createMany(data.map((item) => ({ id: item.id, orgid: item.id, code: item.code, name: item.name })));
};

const checkPuc = async (lastSync: string | number) => {
  const newData = await pucs.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("pucs");
  const pucsGeneric = genericApi<puctype>("pucs");
  const data = await pucs.all();
  await pucsGeneric.createMany(data.map((item) => ({ id: item.id, orgid: item.id, producer_id: item.producer_id, code: item.code })));
};

const checkOrchard = async (lastSync: string | number) => {
  const newData = await orchards.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("orchards");
  const orchardsGeneric = genericApi<orchardtype>("orchards");
  const data = await orchards.all();
  await orchardsGeneric.createMany(data.map((item) => ({ id: item.id, orgid: item.id, puc_id: item.puc_id, code: item.code })));
};

const checkTargets = async (lastSync: string | number) => {
  const newData = await target.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("targetmarkets");
  const targetmarketsGeneric = genericApi<targetmarkettype>("targetmarkets");
  const result = await targetsDistinctMarketsCountries();
  await targetmarketsGeneric.createMany(result.data.map((item) => ({ id: item.id, market: item.market, country: item.country })));
};

const checkPHTreatment = async (lastSync: string | number) => {
  const newData = await phtreatment.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("phtreatments");
  const phtreatmentsGeneric = genericApi<phtreatmenttype>("phtreatments");
  const result = await phtreatment.all();
  await phtreatmentsGeneric.createMany(
    result.map((item) => ({
      id: item.id,
      commoditycode: item.commoditycode,
      exportmarket: item.exportmarket,
      mrl: item.mrl,
      activeingredient: item.activeingredient,
      product: item.product,
      waterapplication100: item.waterapplication100,
      waxapplication100: item.waxapplication100,
      comments: item.comments,
    })),
  );
};

const checkPHLabels = async (lastSync: string | number) => {
  const newData = await phlabel.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("phlabels");
  const phlabelsGeneric = genericApi<phlabeltype>("phlabels");
  const result = await phlabel.all();
  await phlabelsGeneric.createMany(result.map((item) => ({ id: item.id, market: item.market, item: item.item, substance: item.substance, wording: item.wording })));
};

const checkInspectionLabelRequirements = async (lastSync: string | number) => {
  const newData = await inspectionlabelrequirements.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("inspectionlabelreqs");
  const inspectionlabelreqsGeneric = genericApi<inspectionlabelreqtype>("inspectionlabelreqs");
  const result = await inspectionlabelrequirements.all();
  await inspectionlabelreqsGeneric.createMany(
    result.map((item) => ({
      id: item.id,
      market: item.market,
      country: item.country,
      requiredconsignee: item.requiredconsignee,
      requiredcountry: item.requiredcountry,
    })),
  );
};

const checkSpecialMarket = async (lastSync: string | number) => {
  const newData = await specialmarkets.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("specialmarkets");
  const specialmarketsGeneric = genericApi<specialmarkettype>("specialmarkets");
  const data = await specialmarkets.all();
  await specialmarketsGeneric.createMany(data.map((item) => ({ id: item.id, market: item.market })));
};

const checkVarieties = async (lastSync: string | number) => {
  if (lastSync != 0 && lastSync != "0") return;
  if (!navigator.onLine) return;
  localStorage.removeItem("varieties");
  const varietiesGeneric = genericApi<varietiestype>("varieties");
  const result = await varietyFull();
  await varietiesGeneric.createMany(
    result.data.map((item) => ({
      id: item.id,
      orgid: item.id,
      code: item.code,
      name: item.name,
      commodity_id: item.commodity_id,
      commodity: item.commodity,
      commodity_name: item.commodity_name,
    })),
  );
};

const checkWeeks = async (lastSync: string | number) => {
  const newData = await weeks.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("weeks");
  const weeksGeneric = genericApi<weekstype>("weeks");
  const data = await weeks.all();
  await weeksGeneric.createMany(data.map((item) => ({ id: item.id, orgid: item.id, week: item.week, period_start: item.period_start, period_end: item.period_end })));
};

const checkLabTest = async (lastSync: string | number) => {
  const newData = await labtest.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("labtest");
  const labtestGeneric = genericApi<labtesttype>("labtest");
  const result = await labtestFull();
  await labtestGeneric.createMany(
    result.data.map((item) => ({
      id: item.id,
      orgid: item.id,
      sampledate: item.sampledate,
      sampledateend: item.sampledateend,
      lab: item.lab,
      producer_id: item.producer_id,
      producer_code: item.producer_code,
      producer_name: item.producer_name,
      puc_id: item.puc_id,
      producerpuc_code: item.producerpuc_code,
      orchard_id: item.orchard_id,
      producerorchard_code: item.producerorchard_code,
      variety_id: item.variety_id,
      variety_code: item.variety_code,
      variety_name: item.variety_name,
      labtesttype_id: item.labtesttype_id,
      labtesttype_detail: item.labtesttype_detail,
      approved: item.approved,
    })),
  );
};

const checkProducerPH = async (lastSync: string | number) => {
  const newData = await producerph.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem(producerphStorageName);
  const producerphGeneric = genericApi<producerphtype>(producerphStorageName);
  const result: [GetProducerPHFullDetailType] = await getProducerPHFullDetail(0);
  await producerphGeneric.createMany(
    result.map((item) => ({
      id: item.id.toString(),
      orgid: item.id,
      producer_id: item.producer_id,
      validfrom: item.validfrom,
      validto: item.validto,
      producerphtype_detail: item.producerphtype_detail,
      commoditycode: item.commoditycode,
      exportmarket: item.exportmarket,
      mrl: item.mrl,
      activeingredient: item.activeingredient,
      product: item.product,
      waterapplication100: item.waterapplication100,
      waxapplication100: item.waxapplication100,
      comments: item.comments,
    })),
  );
};

const getPackFull = async () => {
  if (!navigator.onLine) return;
  localStorage.removeItem("pack");
  const packGeneric = genericApi<PackGenericType>("pack");
  const result = await pack.all();
  await packGeneric.createMany(
    result.map((item) => ({
      id: item.id,
      carton: item["No Cartons"],
      code: item.code,
      weight: item.packagingweight,
    })),
  );
};

const getOrdersFullDetail = async (idlist: Array<string | number>) => {
  if (!navigator.onLine) return;
  const idliststring = idlist.join(",");
  const piinstructionGeneric = genericApi<piinstructiontype>("piinstructions");
  const result = await getOrders(idliststring);
  await piinstructionGeneric.createMany(
    result.data.map((item) => ({
      id: item.id,
      orgid: item.id,
      OrderNumber: item.OrderNumber,
      Variety_Name: item.Variety_Name,
      Variety_Code: item.Variety_Code,
      piinstruction_id: item.piinstruction_id,
      groupnum: item.groupnum,
      groupline: item.groupline,
      target_region: item.target_region,
      target_market: item.target_market,
      target_country: item.target_country,
      target_currency: item.target_currency,
      classv: item.class,
      count: item.count,
      pack_code: item.pack_code,
      pack_carton: item.pack_carton,
      brand: item.brand,
      inventory_code: item.inventory_code,
      coldroom: item.coldroom,
      pack_id: item.pack_id,
      rowKey: item.rowKey,
      currency: item.target_currency,
      inventory_description: item.inventory_description,
      mgp: item.mgp,
      fob: item.fob,
      rangemin: item.rangemin,
      rangemax: item.rangemax,
      dip: item.dip,
      dipmin: item.dipmin,
      dipmax: item.dipmax,
      volume: item.volume,
      comment: item.comment,
      pack_name: item.pack_name,
    })),
  );
};

const checkOrdersGrouped = async (lastSync: string | number) => {
  const newData = await piorder.newer(lastSync);
  if (!newData.data || newData.data.length === 0) return;
  if (!navigator.onLine) return;
  localStorage.removeItem("piordergrouped");
  localStorage.removeItem("piinstructions");
  const piordergroupedGeneric = genericApi<piordergroupedtype>("piordergrouped");
  const result = await getOrdersGrouped();
  const orderidlist = result.data.map((item) => item.id);
  await piordergroupedGeneric.createMany(
    result.data.map((item) => ({
      id: item.id,
      orgid: item.id,
      producer_id: item.producer_id,
      producer_name: item.producer_name,
      variety: item.variety,
      variety_id: item.variety_id,
      week_id: item.week_id,
      week: item.week,
      revision: item.revision,
      ordernum: item.ordernum,
      commodity: item.commodity,
      updated_at: item.updated_at,
      exchange: item.exchange,
      groupnumber: item.groupnumber,
      exworks: item.exworks,
      fixed: item.fixed,
      local: item.local,
      printdate: item.printdate,
      farm: item.farm,
      comment: item.comment,
    })),
  );
  await Promise.all([getOrdersFullDetail(orderidlist), getPackFull()]);
};

const pushInspections = async (): Promise<void> => {
  const inspectionsGeneric = genericApi<inspectiontype>("inspections");
  const data = await inspectionsGeneric.filterBy("processed", 1);
  if (!data || data.length === 0) return;

  await Promise.all(
    data.map(async (inspectionitem) => {
      await inspectionsGeneric.update(inspectionitem.id, { ...inspectionitem, processed: 2 });

      const newInspectionData = {
        data: {
          number: inspectionitem.id,
          inspectiondate: inspectionitem.inspectiondate,
          producer: inspectionitem.producer,
          puc: inspectionitem.puc,
          orchard: inspectionitem.orchard,
          specialmarket: inspectionitem.specialmarket,
          pickref: inspectionitem.pickref,
          brix: inspectionitem.brix,
          acid: inspectionitem.acid,
          packedmarket: inspectionitem.packedmarket,
          packedcountry: inspectionitem.packedcountry,
          postharvesttreatment: inspectionitem.postharvesttreatment,
          postharvestlabel: inspectionitem.postharvestlabel,
          labelconsignee: inspectionitem.labelconsignee,
          labelcountry: inspectionitem.labelcountry,
          piorder: inspectionitem.piorder,
          facility: inspectionitem.facility,
          juice: inspectionitem.juice,
          username: inspectionitem.username,
          groupnumber: inspectionitem.groupnumber,
          labtest: inspectionitem.labtest,
          degreen: inspectionitem.degreen,
          flag: inspectionitem.flag,
        },
      };

      const newid = await inspections.create(newInspectionData);
      if (!newid[0]) {
        throw new Error(`could not push inspection - ${newInspectionData.data.pickref}`);
      }

      const childPromises: Array<Promise<unknown>> = [];

      if (inspectionitem.inspectionOrders && inspectionitem.inspectionOrders.length > 0) {
        inspectionitem.inspectionOrders.forEach((inspectionordersitem) => {
          const newInspectionOrdersData = {
            data: {
              inspection_id: newid[0],
              piinstruction_id: inspectionordersitem.piinstruction_id,
              labelcarton: inspectionordersitem.labelcarton,
              inventory: inspectionordersitem.inventory,
              weight: inspectionordersitem.weight,
              firmness: inspectionordersitem.firmness,
              buttons: inspectionordersitem.buttons,
              grading: inspectionordersitem.grading,
              shape: inspectionordersitem.shape,
              sizing: inspectionordersitem.sizing,
              color: inspectionordersitem.color,
              blemish: inspectionordersitem.blemish,
              skintexture: inspectionordersitem.skintexture,
              granulation: inspectionordersitem.granulation,
              general: inspectionordersitem.general,
              proposedmarket: inspectionordersitem.proposedmarket,
              proposedcountry: inspectionordersitem.proposedcountry,
              fruitspec: inspectionordersitem.fruitspec,
            },
          };
          childPromises.push(
            inspectionorders.create(newInspectionOrdersData).then((createdid) => {
              if (!createdid[0]) {
                throw new Error("could not push inspection order item");
              }
            }),
          );
        });
      }

      if (inspectionitem.actions && inspectionitem.actions.length > 0) {
        inspectionitem.actions.forEach((inspectionactionsitem) => {
          const actionsImagesArr = inspectionactionsitem.images.map((imageItem) => ({
            ...imageItem,
            base64: LZString.decompress(imageItem.base64),
          }));
          const imagesJSON = JSON.stringify(actionsImagesArr);

          const newInspectionActionsData = {
            data: {
              inspection_id: newid[0],
              piinstruction_id: inspectionactionsitem.piinstruction_id,
              field: inspectionactionsitem.field,
              comments: inspectionactionsitem.comments,
              images: imagesJSON,
            },
          };
          childPromises.push(
            inspectionactions.create(newInspectionActionsData).then((createdid) => {
              if (!createdid[0]) {
                throw new Error("could not push inspection action item");
              }
            }),
          );
        });
      }

      await Promise.all(childPromises);
    }),
  );
};

const validateInspectionSync = async (): Promise<void> => {
  const inspectionsGeneric = genericApi<inspectiontype>("inspections");
  const datalocal = await inspectionsGeneric.filterBy("processed", 2);
  if (!datalocal || datalocal.length === 0) return;

  await Promise.all(
    datalocal.map(async (itemlocal) => {
      await piorder.single(itemlocal.piorder);
      const datasql = await findByNumber(itemlocal.id);
      if (!datasql || datasql.length === 0) return;
      if (itemlocal.inspectionOrders && datasql[0].orderscount != itemlocal.inspectionOrders.length) return;
      if (itemlocal.actions && datasql[0].actionscount != itemlocal.actions.length) return;

      await inspectionsGeneric.remove(itemlocal.id);
      if (itemlocal.editReference) {
        const dataToUpdate = { data: { status: 3 } };
        await inspections.update(itemlocal.editReference, dataToUpdate);
      }
    }),
  );
};

export const synchronize = async (): Promise<void> => {
  if (!navigator.onLine) {
    throw new Error("You are offline. Please connect to the internet and try again.");
  }

  // Block sync if the user isn't authenticated. Otherwise every /newer/* call
  // returns 401 but still advances the lastSync watermark below, causing the
  // first post-login sync to silently no-op.
  const me = await whoami();

  try {
    if (!me || !me.username) {
      throw new Error("not_authenticated");
    }
  } catch {
    throw new Error("You must be logged in to sync.");
  }

  const storedLastSync = localStorage.getItem("lastSync");
  // If any canary table is empty, the device is missing data it should always
  // have (purged storage, quota exhaustion, half-finished sync, etc.). Treat
  // the run as a fresh install so every table gets re-pulled from the API.
  const lastSync = isDeviceMissingCriticalData() ? "0" : storedLastSync == null ? "0" : storedLastSync;
  // Capture the watermark up-front so any rows updated *during* the sync are
  // still picked up by the next run. Only commit it after the pull phase
  // succeeds so failed syncs don't poison the watermark.
  const startedAt = moment().utc().valueOf().toString();

  await safe(() => checkUsers(lastSync));
  await safe(() => checkProducers(lastSync));
  await safe(() => checkPuc(lastSync));
  await safe(() => checkOrchard(lastSync));
  await safe(() => checkTargets(lastSync));
  await safe(() => checkPHTreatment(lastSync));
  await safe(() => checkPHLabels(lastSync));
  await safe(() => checkInspectionLabelRequirements(lastSync));
  await safe(() => checkSpecialMarket(lastSync));
  await safe(() => checkVarieties(lastSync));
  await safe(() => checkWeeks(lastSync));
  await safe(() => checkLabTest(lastSync));
  await safe(() => checkProducerPH(lastSync));
  await safe(() => checkOrdersGrouped(lastSync));

  await safe(() => pushInspections());
  await safe(() => validateInspectionSync());

  localStorage.setItem("lastSync", startedAt);
};
